import { LocationDescriptorObject } from 'history';
import { Platform, SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { getDurationNicknameFromMs, renderDateNickname } from '@fullstory/util/date';
import { hostnameFromAbsoluteUrl } from '@fullstory/util/url';
import { UID } from '@fullstory/util/uid';
import {
  generateRoutePath,
  ORG_ROUTE,
  SessionExpanderRoute,
  SessionOrMarkedPlayback,
} from '@fullstory/routes';
import { SessionExpr, userSessionId } from '@fullstory/playback-model';
import {
  getOfflineServerVaribleStrings,
  lastMatchingDeviceToPlatformEnum,
  MATCHING_CLIPS,
  MATCHING_SESSIONS,
  SERVER,
  UNKNOWN,
} from '../utils';
import { watchHistory as watchHistorySelector } from '@fullstory/redux';
import { noneDisabled, sessionListSharedToProto } from '@fullstory/search-shared';
import { fromDurationToMilli } from '@fullstory/search-converter/helpers';
import { DurationUnit } from '@fullstory/search-types';
import { Duration, timestamp, Timestamp } from '@fullstory/proto/google/protobuf';
import { isOnline } from '@fullstory/util/session';
import { intl } from '@fullstory/intl';
import moment from 'moment';
import { UserDetailsResponse } from '@fullstory/client-api';
import { SegmentedControlOptionType } from '@fullstory/components';
import { useMemo } from 'react';
import { SessionListItem } from '../types';

const toSeconds = Duration.toSeconds;
type UseUserSessionsProps = {
  sessionData: UserDetailsResponse | null;
  userId: string;
  segmentDefinition: SegmentDefinition;
  sessionFilter: SegmentedControlOptionType | null;
  orgId: string;
  watchHistory: ReturnType<typeof watchHistorySelector>;
};
type FakeAppContext = {
  currentSegmentDefinition: () => {
    definition: SegmentDefinition;
    disabled: ReturnType<typeof noneDisabled>;
    name: string;
    description: string;
  };
};

const getSessionExpanderRoute = (
  ctx: FakeAppContext,
  userId: string,
  userIdSessionId: string | null,
  sessionFilterId: string | null | undefined,
): SessionExpanderRoute | null => {
  // sanity check
  if (!userIdSessionId) {
    return null;
  }

  return SessionOrMarkedPlayback(
    ctx,
    userId,
    userIdSessionId,
    sessionFilterId === MATCHING_SESSIONS.id || sessionFilterId === MATCHING_CLIPS.id,
  );
};

const getRoute = (sessionExpanderRoute: SessionExpanderRoute | null, orgId: string) => {
  if (!sessionExpanderRoute) {
    return '';
  }

  return {
    pathname: [generateRoutePath(ORG_ROUTE, { orgId }), ...sessionExpanderRoute.route].join('/'),
    state: sessionExpanderRoute?.state,
  };
};

const getOnlineStatus = (
  lastEventAt: timestamp | null,
  lastEventAtParsed: Date,
  segmentDefinition: SegmentDefinition,
) => {
  if (!lastEventAt) {
    return false;
  }

  return isOnline(
    moment(),
    moment(lastEventAtParsed),
    segmentDefinition.constraints?.completeSessions ?? false,
  );
};

const getLocation = (lastMatchingCity: string, isServerEventOnly: boolean) => {
  if (isServerEventOnly) {
    return SERVER;
  }

  if (lastMatchingCity === '?' || !lastMatchingCity) {
    return UNKNOWN;
  }

  return lastMatchingCity;
};

export const useUserSessions = ({
  sessionData,
  userId,
  segmentDefinition,
  sessionFilter,
  orgId,
  watchHistory,
}: UseUserSessionsProps): SessionListItem[] => {
  const sessions = useMemo<SessionListItem[]>(() => {
    const sessionsProto = sessionData?.Sessions
      ? sessionListSharedToProto(sessionData.Sessions)
      : null;
    // sanity check
    if (!sessionsProto) {
      return [];
    }
    const fakeAppContext = {
      currentSegmentDefinition: () => ({
        definition: segmentDefinition,
        disabled: noneDisabled(),
        name: '',
        description: '',
      }),
    };

    return sessionsProto
      .map((session): SessionListItem => {
        const lastEventAtParsed = session.lastEventAt
          ? Timestamp.mustDate(session.lastEventAt)
          : new Date();
        const isOnline = getOnlineStatus(session.lastEventAt, lastEventAtParsed, segmentDefinition);
        const isWebSession = session.lastMatchingPlatform === Platform.PLATFORM_WEB;
        const hostName = session.lastMatchingPage
          ? hostnameFromAbsoluteUrl(session.lastMatchingPage)
          : '';
        const webBrowser = session.lastMatchingBrowser || UNKNOWN;
        const userIdSessionId =
          session.sessionId !== '0' ? userSessionId(session.userId, session.sessionId) : null;
        const sessionExpanderRoute = getSessionExpanderRoute(
          fakeAppContext,
          userId,
          userIdSessionId,
          sessionFilter?.id,
        );
        const route: LocationDescriptorObject | string = getRoute(sessionExpanderRoute, orgId);
        const isOfflineServerEventOnly = !route;
        const sessionExpr = userIdSessionId
          ? SessionExpr.historyState(userId, userIdSessionId, 0)
          : null;
        const isWatched =
          sessionExpr && watchHistory.data[sessionExpr] != null
            ? watchHistory.data[sessionExpr]!
            : false;
        return {
          id: UID(),
          title: isOnline
            ? intl.formatMessage({ defaultMessage: 'Online', id: 'ccU6l/' })
            : renderDateNickname(lastEventAtParsed),
          eventCount: session.numEvents,
          pageCount: session.numPages,
          length: getDurationNicknameFromMs(
            session.duration
              ? fromDurationToMilli({
                  unit: DurationUnit.second,
                  scalar: toSeconds(session.duration),
                })
              : 0,
          ),
          location: getLocation(session.lastMatchingCity, isOfflineServerEventOnly),
          hostOrAppName: isWebSession ? hostName : session.lastMatchingAppName,
          hostOrAppNameTooltip: !isWebSession ? session.lastMatchingAppVersion : '',
          platformIcon: lastMatchingDeviceToPlatformEnum(
            isOfflineServerEventOnly ? 'Server' : session.lastMatchingDevice,
          ),
          platformName: isOfflineServerEventOnly
            ? intl.formatMessage({ defaultMessage: 'Server Event', id: 'Y/hWhL' })
            : session.lastMatchingOperatingSystem,
          isOnline,
          browserOrOs: isWebSession ? webBrowser : session.lastMatchingOperatingSystem || UNKNOWN,
          browserOrOsTooltip: isWebSession ? '' : session.lastMatchingOperatingSystemVersion,
          serverEventName: isOfflineServerEventOnly ? session.lastMatchingEventName : undefined,
          serverEventVars: isOfflineServerEventOnly
            ? session.lastMatchingEventVars
                .map(eventVar => `${eventVar.name}: ${getOfflineServerVaribleStrings(eventVar)}`)
                .join('\n')
            : undefined,
          serverIntegrationName: isOfflineServerEventOnly
            ? session.lastMatchingCaptureSourceIntegration
            : undefined,
          isWatched,
          to: route,
          lastEventAt: session.lastEventAt ? Timestamp.mustDate(session.lastEventAt) : null,
        };
      })
      .filter(session => sessionFilter?.label !== 'All Sessions' || !!session.to);
  }, [
    orgId,
    segmentDefinition,
    sessionData?.Sessions,
    sessionFilter?.id,
    sessionFilter?.label,
    userId,
    watchHistory.data,
  ]);

  return sessions;
};
